import {
  findSimilarProduct,
  getPriceValue,
  isSameProduct,
  ProductType,
} from "./Product";
import { DiscountType } from "../enums/DiscountType";
import {
  DiscountItem,
  ProductDiscount,
  CartItem,
  AdhocDiscountViewModel,
  BookingItemViewModel,
  BundleItemViewModel,
  ComboItemViewModel,
  PrePurchasedItem,
  PurchaseItemViewModel,
  PurchaseViewModel,
  RedeemItemViewModel,
  UpgradeTicketViewModel,
} from "../models";
import { areProductOptionsEqual } from "./Modifier";
import {
  getProductIdOrTicketIdFromProduct,
  arePurchaseItemsEqual,
  getTotalPriceForItem,
} from "./Product";
import { areUpgradedTicketsEqual, isSameUpgradedTicket } from "./TicketUpgrade";
import { isTicket } from "./Ticket";
import { isMembership } from "./MembershipAllocationUtilsHelper";
import {
  convertComboItemsToPurchaseItemViewModel,
  convertBundleItemsToPurchaseItemViewModel,
} from "./DataConverters";
import toast from "react-hot-toast/headless";
import { PosState } from "@unleashed-admin/stores/PosSlice";
import { flattenedItemsInCart } from "./DataFlattener";

export const hasDiscountPriceMismatch = (
  discountItem: DiscountItem,
  productToCheck: ProductType
): boolean => {
  const discountPrice = discountItem.price ?? 0;
  const productPrice = getPriceValue(productToCheck);

  return discountPrice !== productPrice;
};

export const findSimilarProductDiscount = (
  productDiscounts: ProductDiscount[],
  productToCheck: ProductType,
  includePrice = true
) => {
  return productDiscounts.find((currentProductDiscount: ProductDiscount) => {
    if (
      getProductIdOrTicketIdFromProduct(currentProductDiscount.discountItem) !==
      getProductIdOrTicketIdFromProduct(productToCheck)
    ) {
      return false;
    }

    if (
      includePrice &&
      hasDiscountPriceMismatch(
        currentProductDiscount.discountItem,
        productToCheck
      )
    ) {
      return false;
    }

    // Compare combo items for CartItem type (uses comboConfigs)
    if (
      "comboPurchasedItems" in currentProductDiscount &&
      "comboConfigs" in productToCheck &&
      currentProductDiscount.comboPurchasedItems &&
      productToCheck.comboConfigs &&
      currentProductDiscount.comboPurchasedItems.length > 0 &&
      productToCheck.comboConfigs.length > 0
    ) {
      return arePurchaseItemsEqual(
        currentProductDiscount.comboPurchasedItems,
        convertComboItemsToPurchaseItemViewModel(productToCheck.comboConfigs)
      );
    }

    // Compare combo items for ComboItemViewModel type (uses purchaseItems)
    if (
      "comboPurchasedItems" in currentProductDiscount &&
      "purchaseItems" in productToCheck &&
      currentProductDiscount.comboPurchasedItems &&
      productToCheck.purchaseItems &&
      currentProductDiscount.comboPurchasedItems.length > 0 &&
      productToCheck.purchaseItems.length > 0
    ) {
      return arePurchaseItemsEqual(
        currentProductDiscount.comboPurchasedItems,
        productToCheck.purchaseItems
      );
    }

    if (
      "bundlePurchasedItems" in currentProductDiscount &&
      "bundleConfigs" in productToCheck &&
      currentProductDiscount.bundlePurchasedItems &&
      productToCheck.bundleConfigs &&
      currentProductDiscount.bundlePurchasedItems.length > 0
    ) {
      return arePurchaseItemsEqual(
        currentProductDiscount.bundlePurchasedItems,
        convertBundleItemsToPurchaseItemViewModel(productToCheck.bundleConfigs)
      );
    }

    if (
      "productOptions" in currentProductDiscount &&
      "productOptions" in productToCheck &&
      currentProductDiscount.productOptions &&
      productToCheck.productOptions &&
      (currentProductDiscount.productOptions.length > 0 ||
        productToCheck.productOptions.length > 0)
    ) {
      return areProductOptionsEqual(
        currentProductDiscount.productOptions,
        productToCheck.productOptions
      );
    }

    if (
      "upgradedTicket" in currentProductDiscount &&
      currentProductDiscount.upgradedTicket &&
      "upgradedTicketId" in productToCheck
    ) {
      return areUpgradedTicketsEqual(
        currentProductDiscount.upgradedTicket,
        productToCheck
      );
    }

    return (
      getProductIdOrTicketIdFromProduct(currentProductDiscount.discountItem) ===
      getProductIdOrTicketIdFromProduct(productToCheck)
    );
  });
};

export const hasSameProductDiscount = (
  productDiscount: ProductDiscount,
  productToCheck: ProductType,
  includePrice = true
) => {
  return (
    findSimilarProductDiscount([productDiscount], productToCheck, includePrice) !== undefined
  );
};

export const calculateDiscount = (
  row: CartItem,
  discountType: DiscountType,
  discountValue: number,
  includeQuantity = false
) => {
  let amount: number;
  if (discountType === DiscountType.Dollar) {
    amount = includeQuantity
      ? discountValue * row.quantity
      : discountValue;
  } else {
    const price = row.isBookingEvent
      ? (row?.bookingEvent?.maxDiscount ?? -1)
      : getTotalPriceForItem(row, includeQuantity);
    // Use integer-cent rounding to match server-side 2-decimal precision and avoid floating-point errors.
    amount = Math.round((discountValue * Math.round(price * 100)) / 100) / 100;
  }

  const discount = Number(amount.toFixed(2));
  return -Math.abs(discount);
};

export const applyProductDiscount = (
  item: CartItem | PrePurchasedItem,
  productsDiscount: ProductDiscount[],
  discounts?: AdhocDiscountViewModel[]
) => {
  const productDiscount = findSimilarProductDiscount(productsDiscount, item, !isMembership(item));

  if (productDiscount) {
    const discountReasonId =
      discounts?.find(
        (x) =>
          x.discountReasonCode ===
          productDiscount?.discountItem.discountReasonCode
      )?.discountReasonId ?? 0;

    return {
      discountAmount: Math.abs(productDiscount.discountItem.discountAmount),
      discountReasonCode: productDiscount.discountItem.discountReasonCode,
      discountReasonId,
      discountValue: Math.abs(productDiscount.discountItem.discountValue),
      discountType: productDiscount.discountItem.discountType,
    };
  }

  return undefined;
};

export const updateProductDiscountForItemInCart = (
  cartItem: CartItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): CartItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  const matchingPurchaseItem = findSimilarProduct(
    purchasePreviewResponseViewModel.purchaseItems ?? [],
    cartItem,
    !isMembership(cartItem)
  ) as PurchaseItemViewModel;
  if (matchingPurchaseItem) {
    const { adhocDiscount, adhocDiscountError } = matchingPurchaseItem;

    if (adhocDiscountError) {
      toast.error(`${matchingPurchaseItem.name}: ${adhocDiscountError}`);
      return { ...cartItem, discount: undefined };
    }

    if (adhocDiscount) {
      return { ...cartItem, discount: createDiscount(adhocDiscount) };
    }
  }

  return matchingPurchaseItem?.adhocDiscount === undefined &&
    cartItem.discount !== undefined
    ? { ...cartItem, discount: undefined }
    : cartItem;
};

export const updateProductDiscountForCombo = (
  cartItem: CartItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): CartItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  if (!isTicket(cartItem)) {
    const matchingPurchaseItem = findSimilarProduct(
      purchasePreviewResponseViewModel.comboItems ?? [],
      cartItem
    ) as ComboItemViewModel;
    if (matchingPurchaseItem) {
      const { adhocDiscount, adhocDiscountError } = matchingPurchaseItem;

      if (adhocDiscountError) {
        toast.error(`${matchingPurchaseItem.name}: ${adhocDiscountError}`);
        return { ...cartItem, discount: undefined };
      }

      if (adhocDiscount) {
        return { ...cartItem, discount: createDiscount(adhocDiscount) };
      }
    }

    return matchingPurchaseItem?.adhocDiscount === undefined &&
      cartItem.discount !== undefined
      ? { ...cartItem, discount: undefined }
      : cartItem;
  } else {
    return cartItem;
  }
};

export const updateProductDiscountForBundle = (
  cartItem: CartItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): CartItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  if (cartItem.isBundle) {
    const matchingPurchaseItem = findSimilarProduct(
      purchasePreviewResponseViewModel.bundleItems ?? [],
      cartItem
    ) as BundleItemViewModel;
    if (matchingPurchaseItem) {
      const { adhocDiscount, adhocDiscountError } = matchingPurchaseItem;

      if (adhocDiscountError) {
        toast.error(`${matchingPurchaseItem.name}: ${adhocDiscountError}`);
        return { ...cartItem, discount: undefined };
      }

      if (adhocDiscount) {
        return { ...cartItem, discount: createDiscount(adhocDiscount) };
      }
    }

    return matchingPurchaseItem?.adhocDiscount === undefined &&
      cartItem.discount !== undefined
      ? { ...cartItem, discount: undefined }
      : cartItem;
  } else {
    return cartItem;
  }
};

export const updateProductDiscountForBooking = (
  cartItem: CartItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): CartItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  if (cartItem.isBookingEvent) {
    if (
      getProductIdOrTicketIdFromProduct(
        purchasePreviewResponseViewModel.bookingItem
      ) === getProductIdOrTicketIdFromProduct(cartItem)
    ) {
      const matchingPurchaseItem =
        purchasePreviewResponseViewModel.bookingItem as BookingItemViewModel;

      const { adhocDiscount, adhocDiscountError } = matchingPurchaseItem;

      if (adhocDiscountError) {
        toast.error(`${matchingPurchaseItem.name}: ${adhocDiscountError}`);
        return { ...cartItem, discount: undefined };
      }

      if (adhocDiscount) {
        return { ...cartItem, discount: createDiscount(adhocDiscount) };
      }

      return matchingPurchaseItem?.adhocDiscount === undefined &&
        cartItem.discount !== undefined
        ? { ...cartItem, discount: undefined }
        : cartItem;
    }
  }
  return cartItem;
};

export const updateProductDiscountForItemsForRedemption = (
  redeemedItem: PrePurchasedItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): PrePurchasedItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  if (!isTicket(redeemedItem)) {
    const matchingRedeemItem = findSimilarProduct(
      purchasePreviewResponseViewModel.redeemingItems ?? [],
      redeemedItem
    ) as RedeemItemViewModel;

    if (matchingRedeemItem) {
      const { adhocDiscount, adhocDiscountError } = matchingRedeemItem;

      if (adhocDiscountError) {
        return { ...redeemedItem, discount: undefined };
      }

      if (adhocDiscount) {
        return { ...redeemedItem, discount: createDiscount(adhocDiscount) };
      }
    }

    return matchingRedeemItem?.adhocDiscount === undefined &&
      redeemedItem.discount !== undefined
      ? { ...redeemedItem, discount: undefined }
      : redeemedItem;
  }

  return redeemedItem;
};

const handleRedemptionDiscountError = (
  itemsInRedemption: CartItem[],
  purchasePreviewResponseViewModel: PurchaseViewModel
) => {
  itemsInRedemption.forEach((redeemItem: CartItem) => {
    const redeemingItems =
      purchasePreviewResponseViewModel.redeemingItems ?? [];
    if (!isTicket(redeemItem)) {
      const matchingRedeemItem = redeemingItems.filter(
        (item: RedeemItemViewModel) => isSameProduct(item, redeemItem)
      );

      if (matchingRedeemItem.length === 0) return;

      if (matchingRedeemItem[0].adhocDiscountError) {
        toast.error(
          `${matchingRedeemItem[0].name}: ${matchingRedeemItem[0].adhocDiscountError}`
        );
      }
    }
  });
};

export const handleRedemptionDiscounts = (
  state: PosState,
  purchasePreviewResponseViewModel: PurchaseViewModel
) => {
  // Handle errors for redeeming items
  handleRedemptionDiscountError(
    flattenedItemsInCart(state.itemsInCart ?? [], state.itemsForRedemption),
    purchasePreviewResponseViewModel
  );

  // Update discounts for redeeming-items in itemsForRedemption
  state.itemsForRedemption = state.itemsForRedemption.map(
    (redeemedItem: PrePurchasedItem) =>
      updateProductDiscountForItemsForRedemption(
        redeemedItem,
        purchasePreviewResponseViewModel
      )
  );
};

export const updateProductDiscountForUpgradedTickets = (
  cartItem: CartItem,
  purchasePreviewResponseViewModel: PurchaseViewModel
): CartItem => {
  const createDiscount = (adhocDiscount: AdhocDiscountViewModel) => ({
    type: adhocDiscount?.discountType ?? DiscountType.Dollar,
    value: adhocDiscount?.discountValue ?? 0,
    amount: adhocDiscount?.discountAmount ?? 0,
    discountReasonCode: adhocDiscount?.discountReasonCode ?? "",
  });

  if (!isTicket(cartItem)) return cartItem;

  const upgradedTickets =
    purchasePreviewResponseViewModel.upgradedTickets ?? [];
  const matchingTickets = upgradedTickets.filter(
    (ticket: UpgradeTicketViewModel) => isSameUpgradedTicket(ticket, cartItem)
  );

  if (matchingTickets.length === 0) return cartItem;

  if (matchingTickets[0].adhocDiscountError) {
    return { ...cartItem, discount: undefined };
  }
  if (matchingTickets[0].adhocDiscount) {
    return {
      ...cartItem,
      discount: createDiscount(matchingTickets[0].adhocDiscount),
    };
  }

  // If no adhocDiscount or error, clear discount if it was set
  if (cartItem.discount !== undefined) {
    return { ...cartItem, discount: undefined };
  }
  return cartItem;
};

const handleUpgradeTicketsDiscountError = (
  itemsInCart: CartItem[],
  purchasePreviewResponseViewModel: PurchaseViewModel
) => {
  itemsInCart.forEach((item: CartItem) => {
    const upgradedTickets =
      purchasePreviewResponseViewModel.upgradedTickets ?? [];
    const matchingTickets = upgradedTickets.filter(
      (ticket: UpgradeTicketViewModel) => isSameUpgradedTicket(ticket, item)
    );

    if (matchingTickets.length === 0) return;

    if (matchingTickets[0].adhocDiscountError) {
      toast.error(`${item.name}: ${matchingTickets[0].adhocDiscountError}`);
    }
  });
};

export const handleUpgradeTicketsDiscounts = (
  state: PosState,
  purchasePreviewResponseViewModel: PurchaseViewModel
) => {
  // Handle errors for upgraded tickets
  handleUpgradeTicketsDiscountError(
    flattenedItemsInCart(state.itemsInCart ?? [], state.itemsForRedemption),
    purchasePreviewResponseViewModel
  );

  // Update discounts for upgraded-tickets in itemsInCart
  state.itemsInCart = state.itemsInCart?.map((cartItem: CartItem) => {
    if (cartItem.isUpgradedCartTicket) {
      return updateProductDiscountForUpgradedTickets(
        cartItem,
        purchasePreviewResponseViewModel
      );
    }
    return cartItem;
  });
};
